import React, { useMemo, useState } from 'react';
import {
  Box,
  Card,
  CardContent,
  Divider,
  LinearProgress,
  Skeleton,
  Tooltip,
  Typography,
} from '@mui/material';
import { ACTIVE_JOB_STATUSES, JobTableType, StatusValue } from 'api/jobApi';
import { useQuery } from '@tanstack/react-query';
import { useNavigate } from 'react-router';
import { OverviewAPI } from '../../../api';
import { getStatusColor } from 'util/statusColors';
import { buildJobListUrl } from '../jobListUrlParams';
import StatusFilterSelect, { isActiveSelection } from './StatusFilter';
import DateRangeFilter from './DateRangeFilter';
import {
  cardSx,
  chartTokens,
  formatCount,
  subtitleSx,
  tabularNumSx,
  titleSx,
} from './dashboardChartTokens';

const statusColors = {
  [StatusValue.NOT_STARTED]: getStatusColor(StatusValue.NOT_STARTED, 200),
  [StatusValue.IN_PROGRESS]: getStatusColor(StatusValue.IN_PROGRESS, 700),
  [StatusValue.ON_HOLD]: getStatusColor(StatusValue.ON_HOLD, 300),
  [StatusValue.APPROVAL_PENDING]: getStatusColor(
    StatusValue.APPROVAL_PENDING,
    700
  ),
  [StatusValue.TO_BE_INVOICED]: getStatusColor(StatusValue.TO_BE_INVOICED, 600),
  [StatusValue.COMPLETED]: getStatusColor(StatusValue.COMPLETED, 800),
  [StatusValue.CANCELLED]: getStatusColor(StatusValue.CANCELLED, 700),
  [StatusValue.CUSTOMER_CONTACTED]: getStatusColor(
    StatusValue.CUSTOMER_CONTACTED,
    500
  ),
  [StatusValue.INITIAL_REPORT_SUBMITTED]: getStatusColor(
    StatusValue.INITIAL_REPORT_SUBMITTED,
    700
  ),
  [StatusValue.PROGRESS_REPORT_SUBMITTED]: getStatusColor(
    StatusValue.PROGRESS_REPORT_SUBMITTED,
    700
  ),
  [StatusValue.INVOICED]: getStatusColor(StatusValue.INVOICED, 800),
};

interface Props {
  clientId?: string;
  label?: string;
  coordinatorId?: string;
}

const JobsByStatusChart = ({ clientId, label, coordinatorId }: Props) => {
  const navigate = useNavigate();
  const [startDate, setStartDate] = useState<string | undefined>(undefined);
  const [endDate, setEndDate] = useState<string | undefined>(undefined);
  const [rangeSummary, setRangeSummary] = useState('all time');
  // Active statuses by default, matching the coordinator card. Anything else
  // is one click away in the filter.
  const [selectedStatus, setSelectedStatus] =
    useState<StatusValue[]>(ACTIVE_JOB_STATUSES);

  /**
   * Drill-through to the job list for one status slice. The All tab is used
   * because a slice can be a closed status (e.g. Completed) that the open
   * tab would contradict, and the range selector has no URL equivalent.
   */
  const drillToStatusJobs = (status: StatusValue) =>
    navigate(
      buildJobListUrl({
        tab: JobTableType.ALL,
        status: [status],
        coordinator: coordinatorId ? [coordinatorId] : undefined,
      })
    );

  const { isFetching, data: fetchedData } = useQuery(
    ['job-status-by-period', clientId, coordinatorId, startDate, endDate],
    () =>
      OverviewAPI.fetchJobStatusByPeriod(
        clientId,
        coordinatorId,
        startDate,
        endDate
      )
  );

  const segments = useMemo(
    () =>
      (fetchedData ?? [])
        .map((slice) => ({
          id: slice.id as StatusValue,
          value: Number(slice.value) || 0,
        }))
        .filter(
          (slice) =>
            slice.value > 0 &&
            (!selectedStatus.length || selectedStatus.includes(slice.id))
        )
        .sort((a, b) => b.value - a.value),
    [fetchedData, selectedStatus]
  );

  const total = segments.reduce((sum, slice) => sum + slice.value, 0);
  const jobsLabel = isActiveSelection(selectedStatus) ? 'ongoing job' : 'job';
  // coordinatorId is only ever set to narrow the chart to one person's own
  // jobs (see PMGridItems/TechnicianGridItems) — its absence always means
  // this is the organization-wide view, whatever title the caller passes.
  const scopeLabel = coordinatorId ? 'your jobs' : 'across the organization';

  return (
    <Card sx={cardSx}>
      {isFetching && <LinearProgress />}
      <CardContent
        sx={{
          flex: 1,
          padding: { xs: 2.5, md: 3 },
          display: 'flex',
          flexDirection: 'column',
        }}>
        <Box
          sx={{
            display: 'flex',
            flexWrap: 'wrap',
            gap: 1.5,
            alignItems: 'flex-start',
            justifyContent: 'space-between',
          }}>
          <Box>
            <Typography sx={titleSx}>{label ?? 'Jobs by Status'}</Typography>
            <Typography sx={subtitleSx}>
              {`${formatCount(total)} ${jobsLabel}${
                total === 1 ? '' : 's'
              } ${rangeSummary} · ${scopeLabel}`}
            </Typography>
          </Box>
          <Box
            sx={{
              display: 'flex',
              flexWrap: 'wrap',
              gap: 1,
              alignItems: 'center',
            }}>
            <StatusFilterSelect
              value={selectedStatus}
              onChange={setSelectedStatus}
              variant="summary"
            />
            <DateRangeFilter
              onChange={(start, end, summary) => {
                setStartDate(start);
                setEndDate(end);
                setRangeSummary(summary);
              }}
            />
          </Box>
        </Box>

        <Divider sx={{ borderColor: chartTokens.divider, marginY: 2.5 }} />

        {isFetching && !segments.length ? (
          <>
            <Skeleton variant="rectangular" height={14} sx={{ borderRadius: 7 }} />
            <Skeleton height={120} />
          </>
        ) : !segments.length ? (
          <Box
            sx={{
              flex: 1,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}>
            <img
              alt="blank canvas"
              src="/images/undraw_blank_canvas_3rbb.svg"
              style={{ width: '60%', maxWidth: 220 }}
            />
          </Box>
        ) : (
          <>
            <Box
              sx={{
                display: 'flex',
                gap: '2px',
                height: 14,
                borderRadius: '7px',
                overflow: 'hidden',
                backgroundColor: chartTokens.divider,
              }}>
              {segments.map((slice) => (
                <Tooltip
                  key={slice.id}
                  title={`${slice.id}: ${formatCount(slice.value)} — click to view jobs`}>
                  <Box
                    onClick={() => drillToStatusJobs(slice.id)}
                    sx={{
                      width: `${(slice.value / total) * 100}%`,
                      minWidth: 3,
                      backgroundColor: statusColors[slice.id],
                      cursor: 'pointer',
                      transition: 'opacity 200ms ease',
                      '&:hover': { opacity: 0.8 },
                    }}
                  />
                </Tooltip>
              ))}
            </Box>

            <Box
              sx={{
                display: 'grid',
                gridTemplateColumns: {
                  xs: 'minmax(0, 1fr)',
                  sm: 'repeat(2, minmax(0, 1fr))',
                },
                columnGap: 3,
                rowGap: 1.5,
                marginTop: 3,
              }}>
              {segments.map((slice) => (
                <Box
                  key={slice.id}
                  role="button"
                  tabIndex={0}
                  onClick={() => drillToStatusJobs(slice.id)}
                  onKeyDown={(event) => {
                    if (event.key === 'Enter' || event.key === ' ') {
                      event.preventDefault();
                      drillToStatusJobs(slice.id);
                    }
                  }}
                  sx={{
                    display: 'flex',
                    alignItems: 'flex-start',
                    gap: 1,
                    minWidth: 0,
                    cursor: 'pointer',
                    borderRadius: '6px',
                    // Negative margin offsets the hover padding so rows keep
                    // their alignment inside the legend grid.
                    margin: -0.5,
                    padding: 0.5,
                    transition: 'background-color 150ms ease',
                    '&:hover': { backgroundColor: chartTokens.accentMuted },
                  }}>
                  <Box
                    sx={{
                      flexShrink: 0,
                      width: 10,
                      height: 10,
                      marginTop: '4px',
                      borderRadius: '3px',
                      backgroundColor: statusColors[slice.id],
                    }}
                  />
                  <Typography
                    sx={{
                      flex: 1,
                      fontSize: 13,
                      lineHeight: 1.35,
                      color: chartTokens.body,
                    }}>
                    {slice.id}
                  </Typography>
                  <Typography
                    sx={{
                      fontSize: 13.5,
                      fontWeight: 700,
                      color: chartTokens.heading,
                      ...tabularNumSx,
                    }}>
                    {formatCount(slice.value)}
                  </Typography>
                </Box>
              ))}
            </Box>
          </>
        )}
      </CardContent>
    </Card>
  );
};

export default JobsByStatusChart;
